import { profileConflictMessage, profileSectionConflicts } from "@proof/core";
import { useEffect, useRef, useState } from "react";
import {
  FormConfigurationSession,
  formConfiguration,
  readFormConfigurationUpload,
  type FormConfiguration,
  type LegacyEnvelope,
} from "@proof/legacy";
import { Configuration } from "./Configuration";
import { AuthoringServicesProvider, type AuthoringServices } from "./services";
import { Button, Control } from "./components";

export interface FormConfigurationEditorProps {
  /** Native form configuration or an ESM table envelope. No saved project is required. */
  value?: unknown;
  projectId?: string | number;
  onChange?: (value: FormConfiguration, esm: LegacyEnvelope) => void;
  onSave?: (output: {
    definition: FormConfiguration;
    esm: LegacyEnvelope;
  }) => void | Promise<void>;
  services?: AuthoringServices;
  esmEnabled?: boolean;
}
export function FormConfigurationEditor(props: FormConfigurationEditorProps) {
  const [session, setSession] = useState(
    () =>
      new FormConfigurationSession(props.value, { projectId: props.projectId }),
  );
  const source = useRef({ value: props.value, projectId: props.projectId });
  const [, setRevision] = useState(0),
    [sourceVersion, setSourceVersion] = useState(0),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState(session.warnings);
  const generation = useRef(0);
  const [importProjectId, setImportProjectId] = useState("");
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  // Replace the source atomically when the host supplies a new snapshot.
  if (
    source.current.value !== props.value ||
    source.current.projectId !== props.projectId
  ) {
    const next = new FormConfigurationSession(props.value, {
      projectId: props.projectId,
    });
    source.current = { value: props.value, projectId: props.projectId };
    generation.current++;
    setSession(next);
    setSourceVersion((r) => r + 1);
    setWarnings(next.warnings);
    setMessage("");
  }
  const download = (name: string, value: unknown) => {
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <AuthoringServicesProvider services={props.services}>
      <section
        className="proof-authoring integration-editor-window"
        aria-label="Standalone form configuration"
      >
        <div className="panel padded">
          <h2>Standalone form configuration</h2>
          <p>
            Design fields, form layout, reusable forms, rules and part overrides
            without a saved project. Load Pageflex XML/.pf or ZIP,
            {props.esmEnabled ? " ESM tables, or " : " or "}form configuration
            JSON.
          </p>
          <Control label="Configuration name">
            <input
              readOnly={!props.services?.editorPresentation?.allowProjectRename}
              value={session.value.name}
              onChange={(event) => {
                try {
                  session.update({
                    ...session.value,
                    name: event.target.value,
                  });
                  setRevision((r) => r + 1);
                  generation.current++;
                  props.onChange?.(session.value, session.exportESM());
                } catch (error) {
                  setMessage((error as Error).message);
                }
              }}
            />
          </Control>
          {props.esmEnabled && (
            <Control label="ESM project ID (optional)">
              <input
                value={importProjectId}
                placeholder="For uploads containing multiple projects"
                onChange={(event) => setImportProjectId(event.target.value)}
              />
            </Control>
          )}
          <div className="button-row">
            <label className="button">
              Upload source
              <input
                aria-label="Upload form source"
                className="sr-only"
                type="file"
                accept=".json,.xml,.pf,.zip"
                disabled={busy}
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (!file) return;
                  const ticket = ++generation.current;
                  setBusy(true);
                  setMessage("");
                  try {
                    const imported = await readFormConfigurationUpload(file, {
                      projectId: importProjectId || props.projectId,
                    });
                    if (ticket !== generation.current) return;
                    setSession(imported.session);
                    setWarnings(imported.warnings);
                    setSourceVersion((r) => r + 1);
                    props.onChange?.(
                      imported.session.value,
                      imported.session.exportESM(),
                    );
                    setMessage(`Loaded ${file.name}`);
                  } catch (error) {
                    if (ticket === generation.current)
                      setMessage((error as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </label>
            <Button
              disabled={busy}
              onClick={() => download("form-configuration.json", session.value)}
            >
              Export configuration JSON
            </Button>
            {props.esmEnabled && (
              <Button
                disabled={busy}
                onClick={() =>
                  download("esm-form-tables.json", session.exportESM())
                }
              >
                Export ESM tables
              </Button>
            )}
          </div>
          <p role="status">{busy ? "Processing…" : message}</p>
          {!!warnings.length && (
            <details>
              <summary>Import notes ({warnings.length})</summary>
              <ul>
                {warnings.map((warning, i) => (
                  <li key={i}>{warning}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <Configuration
          key={sourceVersion}
          project={session.editorProject}
          notify={setMessage}
          onChange={(next) => {
            session.update({
              ...formConfiguration(next),
              ...(session.value.pageflexSourceXml !== undefined
                ? { pageflexSourceXml: session.value.pageflexSourceXml }
                : {}),
            });
            setRevision((r) => r + 1);
            generation.current++;
            props.onChange?.(session.value, session.exportESM());
          }}
        />
        <footer className="integration-editor-footer">
          {" "}
          {props.onSave && (
            <Button
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setMessage("");
                const ticket = generation.current;
                const conflicts = profileSectionConflicts(session.editorProject);
                if (conflicts.length) {
                  setMessage(
                    `Resolve the global profile sections before saving: ${profileConflictMessage(conflicts)}.`,
                  );
                  setBusy(false);
                  return;
                }
                try {
                  await props.onSave!({
                    definition: session.value,
                    esm: session.exportESM(),
                  });
                  if (ticket === generation.current)
                    setMessage("Configuration returned to host.");
                } catch (error) {
                  if (ticket === generation.current)
                    setMessage((error as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save to host
            </Button>
          )}
          <Button
            disabled={busy}
            onClick={() => {
              setSession(
                new FormConfigurationSession(props.value, {
                  projectId: props.projectId,
                }),
              );
              setSourceVersion((n) => n + 1);
              generation.current++;
              setMessage("Changes cancelled.");
            }}
          >
            Cancel
          </Button>
        </footer>
      </section>
    </AuthoringServicesProvider>
  );
}
